import { atom } from "jotai";
import { atomWithImmer } from "jotai-immer";

import { formAtom } from "@frontend/lib/form";
import { text } from "@frontend/lib/form/validators";
import { ApiError } from "@frontend/lib/http";
import { type Reference, loadReferences } from "@frontend/lib/references";
import { msg } from "@lingui/core/macro";

import { jobSchema } from "../schemas/job";
import type { JobValues, TaskValues } from "../schemas/job";
import { jobApi } from "../services";
import type { Employee, ProjectContext } from "../types";
import { toJobValues } from "../utils/payload";

export type JobEditorReferences = {
  workers: Employee[];
  functions: Reference[];
  categories: Reference[];
  holidayActions: Reference[];
  billings: Reference[];
  serviceGroups: Reference[];
  taskTypes: Reference[];
  dayLags: Reference[];
  timeLags: Reference[];
};

export type JobEditorState = {
  projectId: number;
  jobId: number | null;
  project: ProjectContext | null;
  references: JobEditorReferences;
  loading: boolean;
  saving: boolean;
  conflict: boolean;
  error: string | null;
};

const emptyReferences: JobEditorReferences = {
  workers: [],
  functions: [],
  categories: [],
  holidayActions: [],
  billings: [],
  serviceGroups: [],
  taskTypes: [],
  dayLags: [],
  timeLags: [],
};

export const emptyTask = (
  serviceGroupId: number | null,
  workCategoryId: number | null,
): TaskValues => ({
  id: null,
  serviceGroupId,
  typeId: null,
  workCategoryId,
  dayLagId: null,
  timeLagId: null,
  // No range: a task with none runs the whole day, and the track is drawn on
  // rather than filled in.
  hours: [],
  recurrence: { rule: "", exceptions: [] },
  deletable: true,
});

export const emptyJob = (): JobValues => ({
  lockVersion: null,
  workerId: null,
  functionId: null,
  categoryId: null,
  holidayActionId: null,
  billingId: null,
  refundLunch: false,
  refundRide: false,
  inContract: false,
  startDate: null,
  stopDate: null,
  tasks: [],
});

export const initialJobEditorState: JobEditorState = {
  projectId: 0,
  jobId: null,
  project: null,
  references: emptyReferences,
  loading: true,
  saving: false,
  conflict: false,
  error: null,
};

export const jobForm = formAtom(jobSchema, emptyJob(), {
  normalizeNull: false,
});

export const jobEditorAtom = atomWithImmer<JobEditorState>(
  initialJobEditorState,
);

export const referencesAtom = atom((get) => get(jobEditorAtom).references);

export type EditedTask = {
  index: number;
  created: boolean;
  origin: DOMRect | null;
};

export const editedTaskAtom = atom<EditedTask | null>(null);

export const addTaskAtom = atom(
  null,
  (get, set, origin: DOMRect | null = null) => {
    const { project } = get(jobEditorAtom);

    const category = get(jobForm.fields.categoryId.atom) ?? null;
    const index = get(jobForm.fields.tasks.valueAtom).length;

    set(jobForm.fields.tasks.insert, {
      value: emptyTask(project?.serviceGroupId ?? null, category),
    });

    set(editedTaskAtom, { index, created: true, origin });
  },
);

export const cancelTaskEditAtom = atom(
  null,
  (get, set, snapshot: TaskValues | null) => {
    const edited = get(editedTaskAtom);

    if (null === edited) {
      return;
    }

    const itemAtom = get(jobForm.fields.tasks.itemAtomsAtom)[edited.index];

    if (itemAtom) {
      if (edited.created) {
        set(jobForm.fields.tasks.remove, itemAtom);
      } else if (null !== snapshot) {
        set(itemAtom, snapshot);
      }
    }

    set(editedTaskAtom, null);
  },
);

export const loadJobEditorAtom = atom(
  null,
  async (_get, set, projectId: number, jobId: number | null) => {
    set(jobEditorAtom, (state) => {
      state.projectId = projectId;
      state.jobId = jobId;
      state.loading = true;
      state.error = null;
      state.conflict = false;
    });

    set(editedTaskAtom, null);

    try {
      const [
        project,
        job,
        workers,
        functions,
        categories,
        holidayActions,
        billings,
        serviceGroups,
        taskTypes,
        dayLags,
        timeLags,
      ] = await Promise.all([
        jobApi.fetchProject(projectId),
        null === jobId ? null : jobApi.fetchJob(projectId, jobId),
        jobApi.fetchEmployees(),
        loadReferences("employeeTypes"),
        loadReferences("employeeCategories"),
        loadReferences("holidayActions"),
        loadReferences("billings"),
        loadReferences("serviceGroups"),
        loadReferences("taskTypes"),
        loadReferences("lagDayTypes"),
        loadReferences("lagTimeTypes"),
      ]);

      set(
        jobForm.actions.setInitial,
        null === job ? emptyJob() : toJobValues(job),
      );

      set(jobEditorAtom, (state) => {
        state.project = project;
        state.references = {
          workers,
          functions,
          categories,
          holidayActions,
          billings,
          serviceGroups,
          taskTypes,
          dayLags,
          timeLags,
        };
        state.loading = false;
      });
    } catch (error: unknown) {
      set(jobEditorAtom, (state) => {
        state.loading = false;
        state.error = message(error);
      });
    }
  },
);

export const saveJobAtom = atom(
  null,
  async (get, set): Promise<number | null> => {
    const { projectId, jobId } = get(jobEditorAtom);

    set(jobEditorAtom, (state) => {
      state.error = null;
      state.conflict = false;
      state.saving = true;
    });

    let savedId: number | null = null;

    try {
      const result = await set(jobForm.actions.submit, async (values) => {
        const saved =
          null === jobId
            ? await jobApi.createJob(projectId, values)
            : await jobApi.updateJob(projectId, jobId, values);

        savedId = saved.id;

        // The record as the API now holds it, lock version included.
        set(jobForm.actions.setInitial, toJobValues(saved));
      });

      if (!result.success) {
        if (0 < result.unbound.length) {
          const paths = result.unbound.join(", ");

          set(jobEditorAtom, (state) => {
            state.error = text(
              msg`The form cannot be saved: a check failed on ${paths}, which this form does not show. Report it.`,
            );
          });
        }

        return null;
      }
    } catch (error: unknown) {
      set(jobEditorAtom, (state) => {
        state.error = message(error);
        state.conflict = error instanceof ApiError && 409 === error.status;
      });

      if (error instanceof ApiError && 0 < error.violations.length) {
        set(jobForm.actions.setErrors, error.violations);
      }

      return null;
    } finally {
      set(jobEditorAtom, (state) => {
        state.saving = false;
      });
    }

    if (null !== savedId) {
      set(jobEditorAtom, (state) => {
        state.jobId = savedId;
      });
    }

    return savedId;
  },
);

function message(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
